import React, { useState, useRef } from 'react';

/**
 * SUIVI DE STAGE — l'éditeur « critères et niveaux » (lot 3.8a)
 * ==============================================================
 * Maquette : #visite-editor et #eval-editor — les DEUX sont le même dessin :
 * nom, « masquer les valeurs numériques », des blocs critère (poignée, libellé,
 * « Commentaire : » bascule, corbeille), leurs niveaux (intitulé + points),
 * l'aperçu des pastilles, le total paramétré, le rappel du commentaire global.
 * ⚠️ Un seul composant pour les deux familles : deux copies auraient divergé.
 *
 * ── ⚠️ LE BROUILLON EST LOCAL, L'ENREGISTREMENT EST UN SEUL GESTE ──────────
 * Rien ne part au serveur avant « Enregistrer le modèle » : on peut ajouter,
 * retirer, réordonner, puis annuler. Le serveur recontrôle tout (au moins deux
 * niveaux par critère, points entiers) et rend la RAISON d'un refus.
 */

const TEXTES = {
    eval: {
        titre: "Grille d'évaluation maître de stage",
        masquer: 'Le maître de stage voit les niveaux sans les points. Les scores restent calculés côté Département Manager.'
    },
    visite: {
        titre: 'Fiche de visite',
        masquer: 'Le tuteur voit les niveaux ("Bien", "Excellent"…) sans les points. Les scores restent calculés côté Département Manager.'
    }
};

/** Les niveaux d'un critère neuf — ceux que la maquette montre au premier critère. */
const NIVEAUX_DEFAUT = () => [
    { id: null, intitule: 'Insuffisant', points: 0 }, { id: null, intitule: 'Passable', points: 1 },
    { id: null, intitule: 'Bien', points: 2 }, { id: null, intitule: 'Très bien', points: 3 }
];
let compteur = 0;
/** Une clé stable pour React, qui ne se confond jamais avec un identifiant de la base. */
const cle = () => `n${++compteur}`;
const avecCles = (criteres) => criteres.map((c) => ({ ...c, _cle: cle(), niveaux: c.niveaux.map((n) => ({ ...n, _cle: cle() })) }));
const maxPoints = (c) => (c.niveaux.length ? Math.max(...c.niveaux.map((n) => Number(n.points) || 0)) : 0);

function StageEditeurCriteres({ famille, modele, modifiable, enCours, erreur, onEnregistrer, onAnnuler, onApercu }) {
    const t = TEXTES[famille];
    const [nom, setNom] = useState(modele.nom || '');
    const [masquer, setMasquer] = useState(modele.masquer_points === true);
    const [criteres, setCriteres] = useState(() => avecCles(modele.criteres?.length ? modele.criteres
        : [{ id: null, libelle: '', commentaire_actif: false, niveaux: NIVEAUX_DEFAUT() }]));
    const glisse = useRef(null);
    const [survol, setSurvol] = useState(null);

    // ⚠️ « gardent leur version » n'est vrai que d'une campagne FIGÉE, pas seulement lancée.
    const lancees = (modele.utilise_dans || []).filter((u) => u.figee);
    const total = criteres.reduce((s, c) => s + maxPoints(c), 0);
    const lectureSeule = !modifiable;

    const poserCritere = (i, champ, v) => setCriteres((cs) => cs.map((c, k) => (k === i ? { ...c, [champ]: v } : c)));
    const poserNiveau = (i, j, champ, v) => setCriteres((cs) => cs.map((c, k) => (k !== i ? c
        : { ...c, niveaux: c.niveaux.map((n, l) => (l === j ? { ...n, [champ]: v } : n)) })));
    const ajouterNiveau = (i) => setCriteres((cs) => cs.map((c, k) => (k !== i ? c
        : { ...c, niveaux: [...c.niveaux, { id: null, _cle: cle(), intitule: '', points: maxPoints(c) + 1 }] })));
    const retirerNiveau = (i, j) => setCriteres((cs) => cs.map((c, k) => (k !== i ? c
        : { ...c, niveaux: c.niveaux.filter((_, l) => l !== j) })));
    const ajouterCritere = () => setCriteres((cs) => [...cs,
        { id: null, _cle: cle(), libelle: '', commentaire_actif: false, niveaux: NIVEAUX_DEFAUT().map((n) => ({ ...n, _cle: cle() })) }]);
    const retirerCritere = (i) => setCriteres((cs) => cs.filter((_, k) => k !== i));

    /** Le glisser-déposer des critères, par la poignée (maquette : `fa-grip-vertical`). */
    const deposer = (cible) => {
        const depart = glisse.current;
        glisse.current = null; setSurvol(null);
        if (depart === null || depart === cible) return;
        setCriteres((cs) => { const copie = [...cs]; const [x] = copie.splice(depart, 1); copie.splice(cible, 0, x); return copie; });
    };
    /** Au clavier aussi : la poignée monte et descend le critère avec les flèches. */
    const deplacer = (i, pas) => setCriteres((cs) => {
        const j = i + pas; if (j < 0 || j >= cs.length) return cs;
        const copie = [...cs]; [copie[i], copie[j]] = [copie[j], copie[i]]; return copie;
    });

    const charge = () => ({
        nom, masquer_points: masquer,
        criteres: criteres.map((c) => ({
            id: c.id, libelle: c.libelle, sous_detail: c.sous_detail || null, commentaire_actif: c.commentaire_actif === true,
            niveaux: c.niveaux.map((n) => ({ id: n.id, intitule: n.intitule, points: Number(n.points) }))
        }))
    });

    return (
        <div className="modele-editor">
            <div className="modele-editor-header">
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--stg-vert-d)' }}>
                    <i className="fa-solid fa-pen" /> {modele.id ? 'Éditeur' : 'Nouveau modèle'} — {t.titre}
                </div>
                <button type="button" className="btn btn-outline btn-sm" onClick={onAnnuler}><i className="fa-solid fa-xmark" /> Fermer</button>
            </div>

            {/* ⚠️ E101 / E115 : ce qu'une modification change, et ce qu'elle ne change pas. */}
            {lancees.length > 0 && (
                <div className="stg-encart stg-encart-alerte">
                    <i className="fa-solid fa-lock" />
                    <span>
                        Ce modèle est utilisé par <b>{lancees.length} campagne{lancees.length > 1 ? 's' : ''} déjà lancée{lancees.length > 1 ? 's' : ''}</b>
                        {' '}({lancees.map((u) => u.libelle).join(', ')}) : elle{lancees.length > 1 ? 's gardent' : ' garde'} la version avec
                        laquelle elle{lancees.length > 1 ? 's ont' : ' a'} commencé. Votre modification vaudra pour les copies de ce processus (Dupliquer, Récupérer N-1).
                    </span>
                </div>
            )}

            <div className="form-group" style={{ marginBottom: 14 }}>
                <label htmlFor="mod-nom">Nom du modèle <span style={{ color: '#dc2626' }}>*</span></label>
                <input id="mod-nom" className="input-sm" style={{ maxWidth: 340 }} value={nom} disabled={lectureSeule}
                       onChange={(e) => setNom(e.target.value)} />
            </div>

            <div className="modele-option">
                <div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>Masquer les valeurs numériques dans le formulaire</div>
                    <div style={{ fontSize: 11, color: 'var(--stg-muted)', marginTop: 2 }}>{t.masquer}</div>
                </div>
                <button type="button" className={'toggle' + (masquer ? ' on' : '')} disabled={lectureSeule}
                        aria-pressed={masquer} aria-label="Masquer les valeurs numériques" onClick={() => setMasquer(!masquer)} />
            </div>

            <div className="modele-sous-titre">Critères &amp; barème</div>
            <div style={{ fontSize: 11, color: 'var(--stg-muted)', marginBottom: 12 }}>
                <i className="fa-solid fa-circle-info" /> Chaque critère a son propre barème. Le total est calculé automatiquement.
            </div>

            {criteres.map((c, i) => (
                <div key={c._cle} className={'critere-block' + (survol === i ? ' survol' : '')}
                     onDragOver={(e) => { e.preventDefault(); if (survol !== i) setSurvol(i); }}
                     onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setSurvol(null); }}
                     onDrop={(e) => { e.preventDefault(); deposer(i); }}>
                    <div className="critere-block-header">
                        <button type="button" className="critere-drag" draggable={!lectureSeule} disabled={lectureSeule}
                                title="Glisser pour réordonner (ou flèches haut / bas)" aria-label={`Déplacer le critère ${i + 1}`}
                                onDragStart={(e) => { glisse.current = i; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }}
                                onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); deplacer(i, -1); } if (e.key === 'ArrowDown') { e.preventDefault(); deplacer(i, 1); } }}>
                            <i className="fa-solid fa-grip-vertical" />
                        </button>
                        <input className="input-sm critere-label" placeholder="Libellé du critère…" value={c.libelle} disabled={lectureSeule}
                               aria-label={`Libellé du critère ${i + 1}`} onChange={(e) => poserCritere(i, 'libelle', e.target.value)} />
                        <span className="critere-commentaire">
                            Commentaire :
                            <button type="button" className={'toggle' + (c.commentaire_actif ? ' on' : '')} disabled={lectureSeule}
                                    aria-pressed={c.commentaire_actif} aria-label={`Commentaire pour le critère ${i + 1}`}
                                    onClick={() => poserCritere(i, 'commentaire_actif', !c.commentaire_actif)} />
                        </span>
                        <button type="button" className="action-btn" title="Supprimer ce critère" disabled={lectureSeule || criteres.length === 1}
                                style={{ color: '#dc2626', borderColor: '#fecaca' }} onClick={() => retirerCritere(i)}>
                            <i className="fa-solid fa-trash" />
                        </button>
                    </div>
                    <div className="niveaux-list">
                        <div className="niveaux-titre">Niveaux (intitulé + valeur en points) :</div>
                        {c.niveaux.map((n, j) => (
                            <div className="niveau-row" key={n._cle}>
                                <input type="text" className="input-sm" placeholder="Intitulé…" value={n.intitule} disabled={lectureSeule}
                                       aria-label={`Intitulé du niveau ${j + 1} du critère ${i + 1}`} onChange={(e) => poserNiveau(i, j, 'intitule', e.target.value)} />
                                <input type="number" className="input-sm" min="0" step="1" value={n.points} disabled={lectureSeule}
                                       aria-label={`Points du niveau ${j + 1} du critère ${i + 1}`} onChange={(e) => poserNiveau(i, j, 'points', e.target.value)} />
                                <span style={{ fontSize: 10, color: 'var(--stg-muted)' }}>pts</span>
                                <button type="button" className="action-btn" title="Retirer ce niveau" disabled={lectureSeule || c.niveaux.length <= 2}
                                        style={{ width: 24, height: 24, fontSize: 11, color: '#dc2626', borderColor: '#fecaca' }} onClick={() => retirerNiveau(i, j)}>
                                    <i className="fa-solid fa-xmark" />
                                </button>
                            </div>
                        ))}
                        <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 4, width: 'fit-content', fontSize: 11 }}
                                disabled={lectureSeule} onClick={() => ajouterNiveau(i)}>
                            <i className="fa-solid fa-plus" /> Ajouter un niveau
                        </button>
                    </div>
                    <div className="niveau-apercu">
                        Aperçu : {c.niveaux.map((n) => <span key={n._cle} className="niveau-chip">{n.intitule || '…'}{masquer ? '' : ` — ${n.points}`}</span>)}
                        · Max <strong>{maxPoints(c)} pts</strong>
                    </div>
                    {c.commentaire_actif && (
                        <div className="critere-comment-preview">
                            <textarea className="input-sm" placeholder="Commentaire pour ce critère… (visible dans le formulaire)" disabled />
                        </div>
                    )}
                </div>
            ))}

            <div className="modele-pied">
                <button type="button" className="btn btn-outline btn-sm" disabled={lectureSeule} onClick={ajouterCritere}>
                    <i className="fa-solid fa-plus" /> Ajouter un critère
                </button>
                <div style={{ fontSize: 12, color: 'var(--stg-muted)' }}>Total paramétré : <strong style={{ color: 'var(--stg-vert-d)' }}>/ {total} pts</strong></div>
            </div>

            <div className="modele-global">
                <i className="fa-solid fa-comment-dots" /> <strong>Commentaire global</strong> — toujours présent en bas du formulaire (non configurable)
            </div>

            {erreur && <div className="stg-encart stg-encart-erreur" style={{ marginTop: 12 }}><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}

            <div className="modele-actions">
                <button type="button" className="btn btn-green btn-sm" disabled={lectureSeule || enCours} onClick={() => onEnregistrer(charge())}>
                    {enCours ? <><i className="fa-solid fa-circle-notch stg-tourne" /> Enregistrement…</> : <><i className="fa-solid fa-check" /> Enregistrer le modèle</>}
                </button>
                <button type="button" className="btn btn-outline btn-sm" onClick={onAnnuler}>Annuler</button>
                <button type="button" className="lien-apercu" onClick={() => onApercu(charge())}>
                    <i className="fa-solid fa-eye" /> Voir l'aperçu
                </button>
            </div>
            {lectureSeule && <div style={{ fontSize: 11, color: 'var(--stg-muted)', marginTop: 6 }}><i className="fa-solid fa-circle-info" /> Lecture seule : votre profil ne permet pas de modifier ce module.</div>}
        </div>
    );
}

export default StageEditeurCriteres;
